/**
 * Main SYNTHÉTIQUE (tests e2e) : le faux flux caméra de Chrome ne contient pas de
 * main. Gestes pilotables : absente, ouverte, qui pince.
 */
import type { HandFrame, Point3 } from './types';

export type SyntheticGesture = 'none' | 'open' | 'pinch' | 'rock' | 'paper' | 'scissors';

const P = (x: number, y: number): Point3 => ({ x, y, z: 0 });

/** Abscisse de la base (MCP) de l'index, du majeur, de l'annulaire et de l'auriculaire. */
const FINGER_X = [0.44, 0.49, 0.54, 0.59] as const;

/**
 * Main entière, paume face caméra, doigts vers le haut : chaque doigt (MCP, PIP, DIP, bout)
 * est tendu ou replié vers la paume (le bout revient alors près du poignet).
 */
function handShape(extended: readonly [boolean, boolean, boolean, boolean]): Point3[] {
  const pts = [P(0.5, 0.85), P(0.44, 0.8), P(0.4, 0.75), P(0.38, 0.71), P(0.37, 0.67)];
  FINGER_X.forEach((x, i) => {
    pts.push(
      P(x, 0.65),
      ...(extended[i]
        ? [P(x, 0.57), P(x, 0.52), P(x, 0.47)]
        : [P(x, 0.6), P(x + 0.01, 0.66), P(x + 0.01, 0.7)]),
    );
  });
  return pts;
}

const SHAPES = {
  rock: [false, false, false, false],
  paper: [true, true, true, true],
  scissors: [true, true, false, false],
} as const;

/** 21 points MediaPipe : poignet (0), bout du pouce (4), de l'index (8), base du majeur (9). */
export function syntheticHand(gesture: Exclude<SyntheticGesture, 'none'>): Point3[] {
  if (gesture !== 'open' && gesture !== 'pinch') return handShape(SHAPES[gesture]);
  const pts = Array.from({ length: 21 }, () => P(0.5, 0.7));
  pts[0] = P(0.5, 0.8);
  pts[9] = P(0.5, 0.6);
  if (gesture === 'open') {
    pts[4] = P(0.4, 0.55);
    pts[8] = P(0.55, 0.45);
  } else {
    pts[4] = P(0.48, 0.5);
    pts[8] = P(0.5, 0.5);
  }
  return pts;
}

export interface HandSource {
  start(): Promise<void>;
  stop(): void;
  subscribe(listener: (frame: HandFrame) => void): () => void;
}

export interface SyntheticHandSource extends HandSource {
  setGesture(gesture: SyntheticGesture): void;
}

export function createSyntheticHandSource({
  fps = 15,
  now = () => performance.now(),
}: { fps?: number; now?: () => number } = {}): SyntheticHandSource {
  const listeners = new Set<(frame: HandFrame) => void>();
  let timer: ReturnType<typeof setInterval> | undefined;
  let gesture: SyntheticGesture = 'none';
  return {
    start() {
      timer ??= setInterval(() => {
        const frame = { t: now(), landmarks: gesture === 'none' ? null : syntheticHand(gesture) };
        for (const l of listeners) l(frame);
      }, 1000 / fps);
      return Promise.resolve();
    },
    stop() {
      clearInterval(timer);
      timer = undefined;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setGesture(g) {
      gesture = g;
    },
  };
}
